import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Header } from "../components/Header";
import { SocialMeta } from "../components/SocialMeta";
import { api } from "../lib/api";
import { ArrowLeft, Check, Copy, Heart, Lock, ShoppingBag, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";
import { useProductVaultCheckout } from "../hooks/useProductVaultCheckout";
import { useFavorites } from "../contexts/FavoritesContext";
import { useProgress } from "../contexts/ProgressContext";
import { RunPanel } from "../components/RunPanel";
import { CategoryIcon } from "../components/CategoryIcon";
import { OpportunityProfilePanel } from "../components/OpportunityProfilePanel";
import { PromptWatermark } from "../components/PromptWatermark";
import { CollapsiblePrompt } from "../components/CollapsiblePrompt";
import { DigitalDownloadsPicker } from "../components/DigitalDownloadsPicker";
import { CinematicTrackPreview } from "../components/CinematicTrackPreview";
import { TrackNotes } from "../components/TrackNotes";
import { CommercialHeader } from "../components/CommercialHeader";
import { SupersededBanner, Scorecard, CommercialBlock, PlansAndMap, TrustBlock, NextTracks, TrackProgressPanel } from "../components/commercial/TrackCommercial";
import { WorkflowReel } from "../components/WorkflowReel";
import { useAuth } from "../contexts/AuthContext";
import { isNewTrack } from "../lib/isNewTrack";
import { VAULT } from "../data/vaultStats";
import { DemandBadge, DemandEvidence } from "../components/DemandBadge";
import { TrackDownloadButton, ModelBadge } from "../components/TrackDownloads";
import { ThirtyDayPlan } from "../components/ThirtyDayPlan";
import { trackEvent } from "../lib/analytics";

export default function CategoryDetail() {
  const { slug } = useParams();
  const { pvEnabled, buying, buyTrack } = useProductVaultCheckout(slug);
  const [category, setCategory] = useState(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const promptsAnchorRef = useRef(null);
  const { isFavorite, toggle } = useFavorites();
  const { isDone, toggle: toggleProgress, countFor } = useProgress();
  const { user } = useAuth();
  const watermarkId = user?.email || user?.name || "";

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get(`/categories/${slug}`)
      .then((res) => {
        if (mounted) setCategory(res.data);
        api.get("/track-states").then((r) => mounted && setReady(!!r.data.states?.[slug]?.ready_to_sell)).catch(() => {});
        trackEvent("TrackViewed", { track: slug, content_name: res.data?.name, content_ids: [slug], surface: "vault" });
      })
      .catch(() => {
        if (mounted) setCategory(false);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  const handleCopy = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      toast.success("Prompt copied to clipboard.");
      setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 2000);
    } catch {
      toast.error("Could not copy. Select the text manually.");
    }
  };

  const handleFavorite = async (idx, prompt) => {
    const result = await toggle(slug, idx, {
      category_name: category.name,
      category_icon: category.icon,
      category_image_icon: category.image_icon,
      prompt_title: prompt.title,
      prompt_body: prompt.body,
    });
    if (result?.error) {
      toast.error(result.error);
      return;
    }
    if (result?.added) toast.success("Saved to favourites.");
    else toast("Removed from favorites.");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500 font-mono text-sm" data-testid="category-loading">
          Loading category...
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="max-w-5xl mx-auto px-6 py-20" data-testid="category-not-found">
          <p className="text-zinc-400 mb-6">This category does not exist.</p>
          <Link to="/vault" className="text-purple-400 hover:text-purple-300 font-mono uppercase tracking-widest text-xs">
            ← Back to vault
          </Link>
        </div>
      </div>
    );
  }

  const trackLocked = !!category.track_locked;
  const scrollToPrompts = () => {
    promptsAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  // Cinematic preview now runs on every track — first-time visitors on Track 1
  // get the same emotional wow as visitors on Track 46.
  const showCinematicPreview = true;

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden" data-testid="category-detail-page">
      <SocialMeta
        title={`${category.name} — Stag Vault`}
        description={`${category.tagline || ""} · Inside Stag Vault — ${VAULT.tracks} AI income tracks, ${VAULT.promptsFmt} prompts, £25 lifetime.`}
        image={`${typeof window !== "undefined" ? window.location.origin : ""}/og/${slug}.jpg`}
        url={`${typeof window !== "undefined" ? window.location.origin : ""}/vault/${slug}`}
      />
      <Header />

      {/* 20-second cinematic reel-style preview for the 8 Feb-16 tracks.
          Falls back to the standard hero below when not eligible. */}
      {showCinematicPreview && (
        <CinematicTrackPreview
          trackNumber={category.order}
          trackName={category.name}
          tagline={category.tagline}
          trackSlug={category.slug}
          previewVideoId={category.preview_video_url || null}
          iconSlot={<CategoryIcon image={category.image_icon} lucide={category.icon} size={96} />}
          onScrollToPrompts={scrollToPrompts}
        />
      )}

      <section className="border-b border-purple-500/10 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 75% 30%, rgba(168,85,247,0.5), transparent 55%)",
          }}
        />
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-20 relative">
          <Link
            to="/vault"
            className="inline-flex items-center gap-2 text-sm tracking-wide text-zinc-400 hover:text-purple-300 transition-colors mb-10"
            data-testid="back-to-vault-link"
          >
            <ArrowLeft size={16} />
            Back to vault
          </Link>

          <div className="flex flex-col items-start sm:flex-row sm:items-center gap-4 sm:gap-6 mb-8 min-w-0">
            <CategoryIcon image={category.image_icon} lucide={category.icon} size={72} />
            <div className="min-w-0 flex-1">
              <div className="text-purple-400 text-sm tracking-[0.25em] uppercase mb-3 inline-flex flex-wrap items-center gap-2 sm:gap-3">
                <span>Track {String(category.order).padStart(2, "0")} / {category.vault_total_tracks || VAULT.tracks}</span>
                {isNewTrack(category.shipped_at) && (
                  <span
                    className="inline-flex items-center gap-1 bg-gradient-to-r from-purple-500/30 to-fuchsia-500/30 border border-purple-400/70 text-purple-100 text-[10px] tracking-[0.25em] uppercase px-2 py-1 leading-none font-bold shadow-[0_0_18px_rgba(168,85,247,0.55)] animate-pulse"
                    data-testid="track-new-badge"
                  >
                    <Sparkles size={10} strokeWidth={2.5} />
                    New this week
                  </span>
                )}
                <DemandBadge signal={category.demand_signal} slug={category.slug} />
              </div>
              <h1
                className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] break-words max-w-full"
                data-testid="category-title"
              >
                {category.name}
              </h1>
            </div>
          </div>

          <p className="font-display text-xl md:text-2xl text-purple-300/90 mb-6 tracking-tight">
            {category.tagline}
          </p>
          <p className="text-zinc-300 text-lg leading-relaxed max-w-3xl" data-testid="category-description">
            {category.description}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4" data-testid="track-ownership-row">
            <TrackDownloadButton slug={category.slug} locked={trackLocked} />
            <ModelBadge testId="track-model-badge" />
            <span className="text-xs text-zinc-500">Run it in the vault, or download it and own it.</span>
          </div>
          <DemandEvidence evidence={category.demand_evidence} slug={category.slug} className="mt-8" />
          <ThirtyDayPlan slug={category.slug} onJumpToPrompt={(i) => document.querySelector(`[data-testid="run-panel-${i}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" })} />
          <WorkflowReel reel={category.workflow_reel} trackName={category.name} promptCount={category.prompts.length} slug={slug} />
          <OpportunityProfilePanel profile={category.opportunity_profile} />
          <CommercialHeader header={category.commercial_header} className="mt-8" />
          <div className="mt-8"><SupersededBanner superseded={category.superseded_by} /></div>
          {category.commercial ? (
            <div className="mt-8 grid gap-4" data-testid="commercial-layer">
              <div className="grid gap-4 lg:grid-cols-[1fr_360px]"><CommercialBlock c={category.commercial} /><Scorecard sc={category.commercial.scorecard} /></div>
              <PlansAndMap c={category.commercial} />
              <TrustBlock t={category.commercial.trust} />
            </div>
          ) : (
            <p className="mt-8 border border-zinc-800 px-4 py-3 text-xs text-zinc-400" data-testid="assessment-pending"><span className="font-semibold text-zinc-200">Commercial assessment pending.</span> This track has not yet had a real Stag Vault commercial review, so no scorecard, revenue map or opportunity rating is shown. {category.superseded_by ? "Use the recommended route above for the reviewed version." : "Reviewed tracks are marked with a purple dot in the vault."}</p>
          )}
          {trackLocked && (
            <div className="mt-8 flex items-start gap-4 border border-purple-500/50 bg-purple-500/[0.06] px-5 py-4" data-testid="track-lock-banner">
              <Lock size={20} className="text-purple-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-purple-300 text-sm tracking-[0.2em] uppercase mb-1">Lifetime Access Track</div>
                <p className="text-zinc-300 text-sm leading-relaxed">
                  Tracks 7–11 are previewable on Free. Unlock copying, AI runs and advanced GTM with Lifetime Access —
                  {" "}<span className="text-purple-300">£25 lifetime</span>, no subscription required.
                </p>
              </div>
              <div className="shrink-0 flex flex-col sm:flex-row gap-2">
                {pvEnabled && (
                  <button
                    type="button"
                    onClick={buyTrack}
                    disabled={buying}
                    className="inline-flex items-center justify-center gap-2 border border-purple-500 text-purple-200 hover:bg-purple-500/10 px-4 py-2 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 disabled:opacity-50"
                    data-testid="track-lock-buy-track"
                  >
                    <ShoppingBag size={14} />
                    {buying ? "…" : "Buy just this track"}
                  </button>
                )}
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center bg-purple-500 hover:bg-purple-400 text-black px-4 py-2 text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300"
                  data-testid="track-lock-upgrade"
                >
                  Upgrade
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
        {/* Digital Downloads · Personal product-launch picker */}
        {slug === "digital-downloads" && category.prompts.length > 0 && (
          <DigitalDownloadsPicker
            prompts={category.prompts}
            onJump={(idx) => {
              const el = document.querySelector(`[data-testid="prompt-card-${idx}"]`);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          />
        )}

        {/* Quick Start sub-track — skipped for Digital Downloads (30-item picker replaces it) */}
        {slug !== "digital-downloads" && category.prompts.length >= 3 && (() => {
          const qsTotal = 3;
          const qsDone = [0, 1, 2].filter((i) => isDone(slug, i)).length;
          const qsComplete = qsDone === qsTotal;
          return (
            <div
              className={`mb-12 border p-5 md:p-6 transition-colors duration-300 ${
                qsComplete
                  ? "border-purple-500/60 bg-purple-500/[0.07]"
                  : "border-purple-500/30 bg-[#0c0710]"
              }`}
              data-testid="quick-start-banner"
            >
              <div className="flex items-start md:items-center gap-4 flex-col md:flex-row">
                <div className={`shrink-0 w-12 h-12 inline-flex items-center justify-center border ${qsComplete ? "border-purple-400 bg-purple-500/20 text-purple-300" : "border-purple-500/40 text-purple-400"}`}>
                  {qsComplete ? <Check size={20} strokeWidth={3} /> : <Zap size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-purple-400 text-[11px] tracking-[0.3em] uppercase mb-1.5">
                    {qsComplete ? "Quick Start · Complete" : "Quick Start · 3 moves to get live"}
                  </div>
                  <p className="text-zinc-200 text-sm md:text-base leading-relaxed">
                    {qsComplete ? (
                      <>You&apos;ve picked your platform, set up Day 1, and got the first-customer plan locked in. Now ship the rest of the track.</>
                    ) : (
                      <>Run the first 3 prompts in order: pick the platform, complete Day-1 setup, then run the first-customer playbook. Most members go from zero to live in under 7 days.</>
                    )}
                  </p>
                  {/* Progress bar — 3 segments */}
                  <div className="mt-3 flex items-center gap-2">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 transition-colors duration-300 ${
                          isDone(slug, i) ? "bg-purple-400" : "bg-zinc-800"
                        }`}
                        data-testid={`quick-start-segment-${i}`}
                      />
                    ))}
                    <span className="ml-2 text-zinc-400 text-xs tracking-[0.2em] uppercase font-mono shrink-0" data-testid="quick-start-progress">
                      {qsDone} / {qsTotal}
                    </span>
                  </div>
                  {qsComplete && (
                    <Link
                      to={`/share/quick-start/${slug}`}
                      className="mt-4 inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-400 text-black px-4 py-2 text-xs tracking-[0.25em] uppercase font-medium transition-colors"
                      data-testid="quick-start-share-cta"
                    >
                      <Sparkles size={14} />
                      Share your win
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        <TrackProgressPanel slug={slug} done={countFor(slug)} total={category.prompts.length} ready={ready} onReady={setReady} commercial={category.commercial} isPaid={!trackLocked} />
        <span className="sr-only" data-testid="track-progress-label">{countFor(slug)} / {category.prompts.length} done</span>

        <div ref={promptsAnchorRef} className="space-y-6" data-testid="prompts-list">
          {category.prompts.map((p, idx) => {
            const fav = isFavorite(slug, idx);
            const locked = !!p.locked;
            const innerCircle = !!p.inner_circle;
            return (
              <article
                key={idx}
                className={`bg-[#0a0a0a] border transition-colors duration-300 group ${locked ? "border-purple-500/30" : "border-zinc-900 hover:border-purple-500/40"}`}
                data-testid={`prompt-card-${idx}`}
                data-locked={locked ? "true" : "false"}
              >
                <div className="flex flex-col gap-4 p-5 md:flex-row md:items-start md:justify-between md:p-8 border-b border-zinc-900">
                  <div className="flex items-start gap-3 md:gap-4">
                    <button
                      onClick={() => toggleProgress(slug, idx)}
                      aria-pressed={isDone(slug, idx)}
                      title={isDone(slug, idx) ? "Mark as not done" : "Mark as done"}
                      disabled={locked}
                      className={`sv-no-min relative mt-1 shrink-0 w-6 h-6 border-2 inline-flex items-center justify-center transition-all duration-200 before:absolute before:-inset-2.5 before:content-[''] ${
                        isDone(slug, idx)
                          ? "bg-purple-500 border-purple-500 text-black"
                          : "border-zinc-700 hover:border-purple-500 text-transparent"
                      } ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid={`progress-checkbox-${idx}`}
                    >
                      <Check size={14} strokeWidth={3} />
                    </button>
                    <div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-zinc-500 text-sm tracking-wide">
                          Prompt {String(idx + 1).padStart(2, "0")}
                        </span>
                        {idx < 3 && (
                          <span className="inline-flex items-center gap-1 bg-purple-500/15 border border-purple-500/40 text-purple-300 text-[10px] tracking-[0.2em] uppercase px-2 py-0.5" data-testid={`quick-start-badge-${idx}`}>
                            <Zap size={10} />
                            Quick Start
                          </span>
                        )}
                        {innerCircle && (
                          <span className="inline-flex items-center gap-1 bg-purple-500/15 border border-purple-500/40 text-purple-300 text-[10px] tracking-[0.2em] uppercase px-2 py-0.5" data-testid={`inner-circle-badge-${idx}`}>
                            <Sparkles size={10} />
                            Advanced GTM
                          </span>
                        )}
                      </div>
                      <h3 className={`font-display text-2xl md:text-3xl font-bold tracking-tight leading-tight ${isDone(slug, idx) ? "text-zinc-400 line-through decoration-purple-500/50" : "text-white"}`}>
                        {p.title}
                      </h3>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0 max-md:pl-0">
                    {locked ? (
                      <div className="flex items-center gap-2">
                        {pvEnabled && (
                          <button
                            type="button"
                            onClick={buyTrack}
                            disabled={buying}
                            className="inline-flex items-center gap-2 border border-purple-500 text-purple-200 hover:bg-purple-500/10 px-4 py-2 text-sm tracking-[0.2em] uppercase font-bold transition-all duration-300 disabled:opacity-50"
                            data-testid={`prompt-buy-track-${idx}`}
                          >
                            <ShoppingBag size={14} />
                            {buying ? "…" : "Buy this track"}
                          </button>
                        )}
                        <Link
                          to="/pricing"
                          className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-400 text-black px-4 py-2 text-sm tracking-[0.2em] uppercase font-bold transition-all duration-300"
                          data-testid={`prompt-upgrade-${idx}`}
                        >
                          <Lock size={14} />
                          Unlock
                        </Link>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => handleFavorite(idx, p)}
                          className={`inline-flex items-center gap-2 border px-4 py-2 text-sm tracking-wide transition-all duration-300 ${
                            fav
                              ? "border-purple-500 bg-purple-500/10 text-purple-300"
                              : "border-zinc-800 hover:border-purple-500 text-zinc-400 hover:text-purple-300"
                          }`}
                          aria-pressed={fav}
                          data-testid={`favorite-prompt-button-${idx}`}
                        >
                          <Heart size={16} fill={fav ? "currentColor" : "none"} />
                          {fav ? "Saved" : "Save"}
                        </button>
                        <button
                          onClick={() => handleCopy(p.body, idx)}
                          className="inline-flex items-center gap-2 border border-zinc-800 hover:border-purple-500 text-zinc-400 hover:text-purple-300 px-4 py-2 text-sm tracking-wide transition-all duration-300"
                          data-testid={`copy-prompt-button-${idx}`}
                        >
                          {copiedIdx === idx ? (
                            <>
                              <Check size={16} />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy size={16} />
                              Copy
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="relative">
                  <CollapsiblePrompt body={p.body} idx={idx} locked={locked} copied={copiedIdx === idx} onCopy={() => handleCopy(p.body, idx)}>
                    {!locked && watermarkId && (
                      <PromptWatermark identifier={watermarkId} className="z-10" />
                    )}
                  </CollapsiblePrompt>
                  {!locked && p.next_step && (
                    <p className="border-t border-zinc-900 bg-[#070710] px-6 py-3 text-xs text-zinc-400 md:px-8" data-testid={`prompt-next-step-${idx}`}><span className="font-mono text-[10px] uppercase tracking-[0.25em] text-purple-300">Next step · </span>{p.next_step}</p>
                  )}
                  {locked && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="bg-black/70 border border-purple-500/50 px-5 py-3 backdrop-blur-sm pointer-events-auto" data-testid={`prompt-lock-overlay-${idx}`}>
                        <div className="flex items-center gap-3">
                          <Lock size={16} className="text-purple-400" />
                          <span className="text-zinc-200 text-sm tracking-wide">
                            {innerCircle ? "Advanced GTM — Lifetime Access only" : "Lifetime Access track"}
                          </span>
                          <Link to="/pricing" className="text-purple-300 hover:text-purple-200 text-xs tracking-[0.25em] uppercase font-bold" data-testid={`prompt-lock-cta-${idx}`}>
                            Upgrade →
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="px-4 md:px-8 py-3 md:py-4 border-t border-zinc-900 bg-[#0a0a0a] flex flex-wrap items-center justify-between gap-2">
                  <span className="text-zinc-500 text-sm">
                    Runs on ChatGPT · Claude · Gemini
                  </span>
                  {locked ? (
                    <Link
                      to="/pricing"
                      className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 text-xs tracking-[0.25em] uppercase font-bold"
                      data-testid={`prompt-run-locked-${idx}`}
                    >
                      <Lock size={12} />
                      Upgrade to run
                    </Link>
                  ) : (
                    <RunPanel initialPrompt={p.body} idx={idx} slug={slug} promptIndex={idx} />
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <TrackNotes notes={category.track_notes} slug={slug} />
        {category.commercial && <div className="mt-10"><NextTracks next={category.commercial.next_tracks} /></div>}

        <div className="mt-16 text-center text-zinc-600 text-sm tracking-[0.2em] uppercase">
          End of track
        </div>
      </section>
    </div>
  );
}
